// src/frontend/src/components/Simulation/FeedbackDisplay.tsx
import React from 'react';
import { ConversationFeedback, FeedbackPoint, Message } from '../../types/simulation';

interface FeedbackDisplayProps {
  feedback: ConversationFeedback;
  messages: Message[];
}

const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({ feedback, messages }) => {
  
  // Function to get score color
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Function to get feedback type color
  const getFeedbackTypeColor = (type: string) => {
    switch (type) {
      case 'positive':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'negative':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'suggestion':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Function to get feedback type icon
  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'negative':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'suggestion':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-6 text-center">Simulation Feedback</h2>
      
      {/* Overall Score */}
      <div className="mb-8 text-center">
        <div className="inline-block bg-gray-100 p-4 rounded-full mb-2">
          <span className={`text-4xl font-bold ${getScoreColor(feedback.overallScore)}`}>
            {feedback.overallScore}/10
          </span>
        </div>
        <p className="text-gray-600">Overall Performance Score</p>
      </div>
      
      {/* Strengths and Areas for Improvement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-3">Strengths</h3>
          <ul className="list-disc pl-5 space-y-2">
            {feedback.strengths.map((strength, index) => (
              <li key={index} className="text-gray-700">{strength}</li>
            ))}
          </ul>
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h3 className="text-lg font-semibold text-red-800 mb-3">Areas for Improvement</h3>
          <ul className="list-disc pl-5 space-y-2">
            {feedback.areasForImprovement.map((area, index) => (
              <li key={index} className="text-gray-700">{area}</li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Key Insights */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3">Key Insights</h3>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <p className="text-gray-700">{feedback.keyInsights}</p>
        </div>
      </div>
      
      {/* Specific Feedback */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3">Specific Feedback</h3>
        <div className="space-y-4">
          {feedback.specificFeedback.map((point: FeedbackPoint, index: number) => (
            <div 
              key={index} 
              className={`p-4 rounded-lg border ${getFeedbackTypeColor(point.type)}`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3 mt-1">
                  {getFeedbackTypeIcon(point.type)}
                </div>
                <div>
                  <p className="text-gray-700">{point.message}</p>
                  
                  {/* Related Messages */}
                  {point.relatedMessages && point.relatedMessages.length > 0 && (
                    <div className="mt-3 bg-white bg-opacity-50 p-3 rounded-md">
                      <p className="text-xs font-medium mb-2">Related Exchange:</p>
                      {point.relatedMessages.map((msgIndex) => {
                        const msg = messages[msgIndex - 1]; // Adjust for 0-based indexing
                        if (!msg) return null;
                        
                        return (
                          <div key={msgIndex} className="mb-2 text-sm">
                            <span className="font-semibold">{msg.role === 'student' ? 'You' : 'Stakeholder'}:</span> {msg.content}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Suggested Approach */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Suggested Approach for Next Time</h3>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-gray-700">{feedback.suggestedApproachForNextTime}</p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDisplay;
